import * as THREE from 'three'
import type { PlayerController } from '../player'
import type { SoundEffects } from '../audio/sfx'
import worldConfig from '../../assets/data/world_config.json'

export type PickupType =
  | 'health_pack'
  | 'shield_booster'
  | 'ammo_bullet'
  | 'ammo_plasma'
  | 'ammo_rocket'
  | 'keycard_red'
  | 'keycard_blue'
  | 'keycard_gold'
  | 'health-tank'
  | 'energy-tank'
  | 'reserve-tank'
  | 'spread-ammo'
  | 'missile-ammo'
  | 'gyrex-ammo'
  | 'cannon-ammo'
  | 'keycard-red'
  | 'keycard-blue'
  | 'keycard-yellow'
  | 'keycard-green'

export interface PickupConfig {
  amount: number
  glowColor: string | number
  bobSpeed: number
  bobHeight: number
  spinSpeed: number
  respawnTime: number
}

export function getPickupConfig(type: string): PickupConfig {
  const pickups = worldConfig.pickups as Record<string, PickupConfig>
  if (pickups[type]) {
    return pickups[type]
  }
  return {
    amount: 1,
    glowColor: '#00ffff',
    bobSpeed: 2.0,
    bobHeight: 0.1,
    spinSpeed: 1.5,
    respawnTime: 0,
  }
}

export interface PickupItem {
  id: string
  type: PickupType
  position: THREE.Vector3
  mesh: THREE.Group
  collected: boolean
  config?: PickupConfig
}

export class PickupManager {
  readonly pickups: PickupItem[] = []
  private readonly scene: THREE.Scene
  private idCounter = 0

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  spawn(type: PickupType, pos: THREE.Vector3): PickupItem {
    const id = `pickup-${++this.idCounter}-${type}`
    const config = getPickupConfig(type)
    const group = new THREE.Group()
    group.position.copy(pos)

    const color = new THREE.Color(config.glowColor as THREE.ColorRepresentation).getHex()

    const coreMat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.8,
      roughness: 0.2,
      metalness: 0.8,
    })
    const capMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.9,
      roughness: 0.3,
    })

    const isCard = type.startsWith('keycard')
    if (isCard) {
      const cardMesh = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.4, 0.05), coreMat)
      group.add(cardMesh)
    } else {
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.5, 16), coreMat)
      const capTop = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.08, 16), capMat)
      capTop.position.y = 0.25
      const capBottom = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.08, 16), capMat)
      capBottom.position.y = -0.25
      group.add(body, capTop, capBottom)
    }

    this.scene.add(group)
    const item: PickupItem = { id, type, position: pos.clone(), mesh: group, collected: false, config }
    this.pickups.push(item)
    return item
  }

  update(player: PlayerController, sfx?: SoundEffects): PickupItem[] {
    const collectedNow: PickupItem[] = []

    for (let i = 0; i < this.pickups.length; i++) {
      const p = this.pickups[i]
      if (!p.collected && player.position.distanceTo(p.position) < 1.2) {
        p.collected = true
        this.scene.remove(p.mesh)
        sfx?.playPickup()
        collectedNow.push(p)

        const cfg = p.config ?? getPickupConfig(p.type)

        if (p.type === 'health_pack') {
          player.vitality.heal(cfg.amount)
        } else if (p.type === 'shield_booster') {
          player.vitality.rechargeEnergy(cfg.amount)
        } else if (p.type === 'ammo_bullet') {
          player.weapons.ammo.addAmmo('spread', cfg.amount)
        } else if (p.type === 'ammo_plasma') {
          player.weapons.ammo.addAmmo('gyrex', cfg.amount)
        } else if (p.type === 'ammo_rocket') {
          player.weapons.ammo.addAmmo('missile', cfg.amount)
        } else if (p.type === 'keycard_red') {
          player.keycards.addKeycard('red')
        } else if (p.type === 'keycard_blue') {
          player.keycards.addKeycard('blue')
        } else if (p.type === 'keycard_gold') {
          player.keycards.addKeycard('yellow')
        } else if (p.type === 'health-tank') {
          player.vitality.addHealthTank()
        } else if (p.type === 'energy-tank') {
          player.vitality.addEnergyTank()
        } else if (p.type === 'reserve-tank') {
          player.vitality.addReserveTank()
        } else if (p.type === 'spread-ammo') {
          player.weapons.ammo.addAmmo('spread', cfg.amount || 25)
        } else if (p.type === 'missile-ammo') {
          player.weapons.ammo.addAmmo('missile', cfg.amount || 5)
        } else if (p.type === 'gyrex-ammo') {
          player.weapons.ammo.addAmmo('gyrex', cfg.amount || 15)
        } else if (p.type === 'cannon-ammo') {
          player.weapons.ammo.addAmmo('cannon', cfg.amount || 10)
        } else if (p.type === 'keycard-red') {
          player.keycards.addKeycard('red')
        } else if (p.type === 'keycard-blue') {
          player.keycards.addKeycard('blue')
        } else if (p.type === 'keycard-yellow') {
          player.keycards.addKeycard('yellow')
        } else if (p.type === 'keycard-green') {
          player.keycards.addKeycard('green')
        }
      }
    }

    return collectedNow
  }

  animate(_alpha = 1.0) {
    const time = performance.now() * 0.003
    for (let i = 0; i < this.pickups.length; i++) {
      const p = this.pickups[i]
      if (!p.collected) {
        const cfg = p.config ?? getPickupConfig(p.type)
        const spinSpeed = cfg.spinSpeed ?? 1.5
        const bobSpeed = cfg.bobSpeed ?? 2.0
        const bobHeight = cfg.bobHeight ?? 0.1
        p.mesh.rotation.y += 0.02 * spinSpeed
        p.mesh.position.y = p.position.y + Math.sin(time * bobSpeed + i) * (bobHeight * 0.02)
      }
    }
  }
}
